import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import { BusinessSubscription } from "../../interfaces/AdminSubscriptionObject";
import { getRequest } from "../http/httpRequest";
import { ApiEnvelopeError, unwrapEnvelope } from "../helpers/ApiEnvelopeHelper";
import { convertBusinessSubscriptionList } from "../converters/backendToUi/adminSubscriptionConvertor";

// Admin -> Businesses -> Subscriptions.
//
// A different service from SubscriptionApiService (/api/v2/subscriptions/*,
// user-facing). Two things to keep in mind here:
//  - GET, not POST, and the filter is a query parameter.
//  - the response envelope is `{errors, status:{code}, contents}`, a third
//    shape - unwrapEnvelope was extended to tolerate it (its `status` branch),
//    which is why this service can still use the shared helper.
const BEARER = true;

const FRIENDLY_MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: "Your session has expired. Please log in again.",
  UNAUTHORIZED: "You don't have access to business subscriptions.",
  ACCESS_DENIED: "You don't have access to business subscriptions.",
  FORBIDDEN: "You don't have access to business subscriptions.",
  HTTP_403: "You don't have access to business subscriptions.",
};

export function toFriendlyAdminSubscriptionError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    return FRIENDLY_MESSAGES[error.errorCode] ?? error.message;
  }
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
}

/**
 * Business subscriptions filtered by state.
 *
 * `statuses` maps 1:1 onto the tab's filter chips and is sent comma-separated
 * (`?status=active,paused`) - filtering is SERVER-side, so the slice caches by
 * the joined filter rather than holding one list and filtering it. An empty
 * array omits the parameter entirely rather than sending `status=`, which the
 * backend would have to interpret.
 */
async function listBusinessSubscriptions(
  statuses: string[]
): Promise<BusinessSubscription[]> {
  const filter = statuses.filter(Boolean).join(",");
  const endpoint = filter
    ? `${ServiceEndpoints.BUSINESS_SUBSCRIPTIONS_LIST}?status=${encodeURIComponent(filter)}`
    : ServiceEndpoints.BUSINESS_SUBSCRIPTIONS_LIST;

  const response = await getRequest(endpoint, BEARER);
  const contents = await unwrapEnvelope<unknown>(response);
  return convertBusinessSubscriptionList(contents);
}

const AdminSubscriptionApiService = {
  listBusinessSubscriptions,
};

export default AdminSubscriptionApiService;
